import { z } from "zod";
/**
 * Time tracking — Feature #5 SSOT.
 *
 * Canon: docs/design-canon/04-shared/ssot.md (Zod first; routes
 * import these and use zValidator), docs/enhancements/feature-
 * sketches-2026-05.md §5.
 */
// ─── Enums ────────────────────────────────────────────────
export const TimeEntrySourceSchema = z.enum(["timer", "manual", "imported"]);
// ─── Time entry ───────────────────────────────────────────
export const TimeEntrySchema = z.object({
    id: z.string().uuid(),
    userEmail: z.string().email(),
    projectId: z.string().uuid().nullable(),
    taskId: z.string().uuid().nullable(),
    description: z.string().nullable(),
    startAt: z.string().datetime(),
    /** Null = currently running. */
    endAt: z.string().datetime().nullable(),
    durationSeconds: z.number().int().nullable(),
    source: TimeEntrySourceSchema,
    billable: z.boolean(),
    hourlyRateSnapshot: z.string().nullable(),
    currency: z.string().length(3),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** Body of `POST /time-tracking/start`. */
export const TimerStartSchema = z.object({
    projectId: z.string().uuid().nullable().optional(),
    taskId: z.string().uuid().nullable().optional(),
    description: z.string().max(2000).optional(),
    billable: z.boolean().default(true),
});
/** Manual entry — start + end (or duration) supplied. */
export const TimeEntryManualCreateSchema = z
    .object({
    projectId: z.string().uuid().nullable().optional(),
    taskId: z.string().uuid().nullable().optional(),
    description: z.string().max(2000).optional(),
    startAt: z.string().datetime(),
    endAt: z.string().datetime(),
    billable: z.boolean().default(true),
})
    .refine((v) => Date.parse(v.endAt) > Date.parse(v.startAt), {
    message: "endAt must be after startAt",
    path: ["endAt"],
});
/** Update — narrow patch. End-time changes recompute duration. */
export const TimeEntryUpdateSchema = z
    .object({
    projectId: z.string().uuid().nullable().optional(),
    taskId: z.string().uuid().nullable().optional(),
    description: z.string().max(2000).nullable().optional(),
    startAt: z.string().datetime().optional(),
    endAt: z.string().datetime().optional(),
    billable: z.boolean().optional(),
})
    .refine((v) => v.startAt === undefined ||
    v.endAt === undefined ||
    Date.parse(v.endAt) > Date.parse(v.startAt), { message: "endAt must be after startAt", path: ["endAt"] });
/** List query — date range + optional filters. */
export const TimeEntryListQuerySchema = z.object({
    fromAt: z.string().datetime(),
    toAt: z.string().datetime(),
    projectId: z.string().uuid().optional(),
    taskId: z.string().uuid().optional(),
    /** Admin/exec only — defaults to caller's email. */
    userEmail: z.string().email().optional(),
});
export const TimeEntryListResponseSchema = z.object({
    entries: z.array(TimeEntrySchema),
    /** Sum of durationSeconds across the returned entries. Null
     *  entries (still-running) are excluded. */
    totalSeconds: z.number().int(),
});
// ─── Running timer state ─────────────────────────────────
/** Returned by `GET /time-tracking/running`. Null `entry` = no running timer. */
export const RunningTimerResponseSchema = z.object({
    entry: TimeEntrySchema.nullable(),
});
// ─── User settings ────────────────────────────────────────
export const UserTimeSettingsSchema = z.object({
    userEmail: z.string().email(),
    defaultHourlyRate: z.string().nullable(),
    defaultCurrency: z.string().length(3),
    /** ISO 8601 weekday: 1 = Mon, 7 = Sun. */
    weekStartsOn: z.number().int().min(1).max(7),
    roundToMinutes: z
        .number()
        .int()
        .refine((n) => [0, 5, 15, 30].includes(n), {
        message: "roundToMinutes must be 0, 5, 15, or 30",
    }),
    updatedAt: z.string().datetime(),
});
export const UserTimeSettingsUpdateSchema = z.object({
    defaultHourlyRate: z.number().nonnegative().nullable().optional(),
    defaultCurrency: z.string().length(3).optional(),
    weekStartsOn: z.number().int().min(1).max(7).optional(),
    roundToMinutes: z
        .number()
        .int()
        .refine((n) => [0, 5, 15, 30].includes(n))
        .optional(),
});
//# sourceMappingURL=time-tracking.js.map